import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, Globe, Truck } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api, errMsg, useFetch } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { waLink, money } from "../lib/shop";
import { Seo } from "../components/site/Seo";

function Page({ eyebrow, title, desc, children, testid }) {
  return (
    <div className="container-x py-12 sm:py-20 max-w-4xl" data-testid={testid}>
      <Seo title={title} description={desc} />
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="font-display text-6xl sm:text-8xl leading-none mt-2">{title}</h1>
      <div className="prose-dark mt-8">{children}</div>
    </div>
  );
}

const Owner = ({ v }) => (!v || String(v).includes("OWNER") ? <span className="owner-note">[OWNER TO CONFIRM]</span> : <b className="text-white">{v}</b>);

export function About() {
  return (
    <Page eyebrow="Our story" title="About Mwezzy" testid="about-page" desc="Mwezzy Footwear is a South African online sneaker and footwear brand. STEP DIFFERENT.">
      <p className="!text-lg !text-white">Mwezzy Footwear is a South African online footwear store built for people who move with intention — and want their shoes to say it first.</p>
      <h2>Step different.</h2>
      <p>We curate sneakers and footwear styles that stand out: clean classics, bold statement pairs and everyday heat. Every pair is carefully selected so you can shop with confidence.</p>
      <h2>Online only. Delivered nationwide.</h2>
      <p>We don't have a physical shop — Mwezzy Footwear is 100% online. You order on our website, pay securely online and your order is delivered by courier to your door, anywhere in South Africa.</p>
      <h2>Real people, real support</h2>
      <p>Questions about sizing, stock or your order? Chat to us on WhatsApp or send us an email — we're here to help.</p>
      <Link to="/shop" className="btn-gold mt-6 not-prose">Shop the collection</Link>
    </Page>
  );
}

export function Contact() {
  const { settings: s } = useStore();
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { toast.success((await api.post("/contact", f)).data.message); setF({ name: "", email: "", phone: "", subject: "", message: "" }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="container-x py-12 sm:py-20 grid lg:grid-cols-2 gap-12" data-testid="contact-page">
      <Seo title="Contact" description="Contact Mwezzy Footwear by WhatsApp or email." />
      <div>
        <p className="eyebrow">Talk to us</p>
        <h1 className="font-display text-6xl sm:text-8xl leading-none mt-2">Contact</h1>
        <p className="font-display text-3xl mt-8">{s?.store_name || "Mwezzy Footwear"}</p>
        <div className="mt-6 space-y-4 text-sm">
          <a href={`mailto:${s?.contact_email}`} className="flex gap-3 items-center" data-testid="contact-email"><Mail className="text-gold" size={20} /> <span><span className="label !mb-0">Email</span>{s?.contact_email}</span></a>
          <a href={waLink(s?.whatsapp_number || "", s?.whatsapp_message)} target="_blank" rel="noreferrer" className="flex gap-3 items-center" data-testid="contact-whatsapp"><MessageCircle className="text-gold" size={20} /> <span><span className="label !mb-0">WhatsApp / Phone</span>{s?.whatsapp_number}</span></a>
          <div className="flex gap-3 items-center"><Globe className="text-gold" size={20} /> <span><span className="label !mb-0">Business model</span>Online Store</span></div>
          <div className="flex gap-3 items-center"><Truck className="text-gold" size={20} /> <span><span className="label !mb-0">Delivery</span>Courier delivery available.</span></div>
        </div>
      </div>
      <form onSubmit={submit} className="card-dark p-6 sm:p-8 space-y-4" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="label">Name</label><input required className="field" value={f.name} onChange={set("name")} data-testid="contact-name-input" /></div>
          <div><label className="label">Email</label><input required type="email" className="field" value={f.email} onChange={set("email")} data-testid="contact-email-input" /></div>
          <div><label className="label">Phone</label><input className="field" value={f.phone} onChange={set("phone")} data-testid="contact-phone-input" /></div>
          <div><label className="label">Subject</label><input required className="field" value={f.subject} onChange={set("subject")} data-testid="contact-subject-input" /></div>
        </div>
        <div><label className="label">Message</label><textarea required minLength={5} rows={5} className="field py-3" value={f.message} onChange={set("message")} data-testid="contact-message-input" /></div>
        <button disabled={busy} className="btn-gold w-full" data-testid="contact-submit-button">{busy ? "Sending..." : "Send message"}</button>
      </form>
    </div>
  );
}

export function FAQ() {
  const { settings: s } = useStore();
  const p = s?.policies || {};
  const faqs = [
    ["How do I place an order?", "Browse the shop, choose your product and select your size, then add it to your cart. Go to checkout, enter your delivery details, choose a delivery option and pay securely online."],
    ["How do I pay?", "Payments are made securely online through iKhokha at checkout. Your card details are entered on iKhokha's secure payment page — we never see or store your card number."],
    ["Where does Mwezzy Footwear deliver?", "Delivery is FREE. Choose Uber delivery to your door if you're in Johannesburg (within 20km, 1–3 days), or PAXI collection from any PEP store nationwide (3–5 days)."],
    ["How long does delivery take?", `Delivery time: ${p.delivery_timeframe && !p.delivery_timeframe.includes("OWNER") ? p.delivery_timeframe : "[OWNER TO CONFIRM]"}. Remote areas may take longer.`],
    ["How do I track my order?", "Use the Track Order page with your order number (e.g. MWZ-10001) and the email address or mobile number used at checkout."],
    ["How do I choose my size?", "All sizes are shown in UK sizing. If you're between sizes, or unsure, message us on WhatsApp before ordering and we'll help you choose."],
    ["Can I exchange my footwear?", `Exchange conditions: ${p.exchange_conditions && !p.exchange_conditions.includes("OWNER") ? p.exchange_conditions : "[OWNER TO CONFIRM]"}. See our Returns & Refunds page for details.`],
    ["What happens if my size is unavailable?", "Sizes that show SOLD OUT can't be ordered. Message us on WhatsApp — we can let you know if your size is coming back."],
    ["How do I contact Mwezzy Footwear?", `WhatsApp us on ${s?.whatsapp_number || "+27 61 516 4287"} or email ${s?.contact_email || "mwezzy.footwear@gmail.com"}.`],
  ];
  return (
    <Page eyebrow="Help centre" title="FAQ" testid="faq-page" desc="Frequently asked questions about ordering, payment, delivery and sizing at Mwezzy Footwear.">
      <Accordion type="single" collapsible className="not-prose">
        {faqs.map(([q, a], i) => (
          <AccordionItem key={q} value={`f${i}`} id={i === 5 ? "sizing" : undefined} className="border-white/10">
            <AccordionTrigger className="text-left text-base sm:text-lg font-bold hover:no-underline hover:text-[#d4af37] py-5" data-testid={`faq-question-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-zinc-300 text-sm sm:text-base leading-relaxed">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Page>
  );
}

export function Shipping() {
  const zones = useFetch("/delivery-zones");
  const { settings: s } = useStore();
  return (
    <Page eyebrow="Courier delivery" title="Shipping & Delivery" testid="shipping-page" desc="Mwezzy Footwear delivers nationwide across South Africa by courier.">
      <p>Mwezzy Footwear is an online-only store. At checkout choose <b className="text-white">Uber delivery</b> to your address (Johannesburg, within 20km) or <b className="text-white">PAXI</b> collection at the PEP store of your choice.</p>
      <h2>Delivery areas & fees</h2>
      <div className="not-prose card-dark divide-y divide-white/10 my-4" data-testid="shipping-zone-table">
        {(zones.data || []).map((z) => (
          <div key={z.id} className="flex justify-between gap-4 p-4 text-sm">
            <div><p className="font-bold">{z.name}</p><p className="text-zinc-400 text-xs">{z.description}{z.eta ? ` · ${z.eta}` : ""}</p></div>
            <div className="text-right font-bold">{z.fee ? money(z.fee) : "FREE"}{z.free_over ? <p className="text-xs text-zinc-400 font-normal">Free over {money(z.free_over)}</p> : null}</div>
          </div>
        ))}
      </div>
      <h2>Delivery times</h2>
      <p>Estimated delivery time: <Owner v={s?.policies?.delivery_timeframe} />. Orders are processed once payment is confirmed.</p>
      <h2>Tracking</h2>
      <p>You can follow your order on the <Link to="/track-order" className="text-gold underline">Track Order</Link> page. Once shipped, a courier tracking number may be added to your order.</p>
      <h2>Incorrect addresses</h2>
      <p>Please double-check your delivery address. Re-delivery due to an incorrect address: <Owner v={null} /></p>
    </Page>
  );
}

export function Returns() {
  const { settings: s } = useStore();
  const p = s?.policies || {};
  return (
    <Page eyebrow="Policy" title="Returns & Refunds" testid="returns-page" desc="Returns, exchanges and refunds at Mwezzy Footwear.">
      <p className="owner-note !text-sm !p-3 !block">Highlighted items require the owner's approval before launch. Edit them in Admin › Settings › Store policies.</p>
      <h2>Your rights</h2>
      <p>Nothing in this policy limits your rights under the South African Consumer Protection Act (CPA) and the Electronic Communications and Transactions Act (ECTA), including your right to return defective goods.</p>
      <h2>Return period</h2>
      <p>Returns must be requested within <Owner v={p.return_period_days} /> of delivery.</p>
      <h2>Exchange conditions</h2>
      <p>Exchanges (e.g. for a different size): <Owner v={p.exchange_conditions} /></p>
      <ul><li>Items should be unworn, in original condition and original packaging.</li><li>Proof of purchase (your order number) is required.</li></ul>
      <h2>Return courier costs</h2>
      <p>Return courier fees are paid by: <Owner v={p.return_courier_paid_by} /></p>
      <h2>Refunds</h2>
      <p>Approved refunds are processed via: <Owner v={p.refund_method} />, within <Owner v={p.refund_timeline} />.</p>
      <h2>How to start a return</h2>
      <p>Contact us on WhatsApp ({s?.whatsapp_number}) or email ({s?.contact_email}) with your order number and reason for return.</p>
    </Page>
  );
}

export function Privacy() {
  const { settings: s } = useStore();
  return (
    <Page eyebrow="POPIA" title="Privacy Policy" testid="privacy-page" desc="How Mwezzy Footwear protects your personal information in line with POPIA.">
      <p>Mwezzy Footwear respects your privacy and processes personal information in line with the Protection of Personal Information Act, 2013 (POPIA).</p>
      <h2>What we collect</h2>
      <ul><li>Name, email address and mobile number</li><li>Delivery address and delivery instructions</li><li>Order history and payment status/reference (we do <b>not</b> collect or store card details — payments are processed by iKhokha)</li><li>Newsletter email address if you subscribe</li><li>Messages you send through our contact form</li></ul>
      <h2>Why we collect it</h2>
      <p>To process and deliver your orders, provide customer support, let you track orders, and — only if you opt in — send you news about drops and promotions.</p>
      <h2>Who we share it with</h2>
      <p>Only with service providers needed to fulfil your order: our payment provider (iKhokha) and courier partners. We do not sell your personal information.</p>
      <h2>Security</h2>
      <p>We use encrypted connections (HTTPS), hashed passwords and restricted admin access to protect your information.</p>
      <h2>Your rights</h2>
      <p>You may request access to, correction of, or deletion of your personal information, and unsubscribe from marketing at any time, by contacting {s?.contact_email}.</p>
      <h2>Information Officer</h2>
      <p>Information Officer: <Owner v={null} /> · Contact: {s?.contact_email}</p>
    </Page>
  );
}

export function Terms() {
  const { settings: s } = useStore();
  return (
    <Page eyebrow="Legal" title="Terms & Conditions" testid="terms-page" desc="Terms and conditions for shopping at Mwezzy Footwear.">
      <p className="owner-note !text-sm !p-3 !block">Template terms — please have these reviewed before launch.</p>
      <h2>About us</h2>
      <p>Mwezzy Footwear is an online store operating in South Africa. Contact: {s?.contact_email} · {s?.whatsapp_number}. Registered business details: <Owner v={null} /></p>
      <h2>Orders & pricing</h2>
      <p>All prices are in South African Rand (ZAR) and include VAT where applicable: <Owner v={null} />. An order is confirmed only once payment has been successfully verified. We may cancel and fully refund an order if an item becomes unavailable.</p>
      <h2>Payment</h2>
      <p>Payments are processed securely by iKhokha. We never store your card details.</p>
      <h2>Delivery</h2>
      <p>Orders are delivered by courier. See <Link to="/shipping" className="text-gold underline">Shipping & Delivery</Link>.</p>
      <h2>Returns</h2>
      <p>See <Link to="/returns" className="text-gold underline">Returns & Refunds</Link>.</p>
      <h2>Governing law</h2>
      <p>These terms are governed by the laws of the Republic of South Africa.</p>
    </Page>
  );
}
